import React, { useMemo } from "react";
import * as XLSX from "xlsx";
import { Snapshot } from "../services/animationSnapshotProvider";
import { formatCenterTrendNumber } from "../utils/trendCellDisplay";
import "./AnimationDatasetTable.css";

/** The four metric dimensions shown per keyword row in the table. */
type MetricKey = "volume" | "difficulty" | "ranking" | "trend";

/**
 * Represents a single keyword's row group: its keyword name, anchor flag, and
 * an array of values per metric, each array aligned to the snapshots column order.
 */
type TimelineTableRow = {
  keyword: string;
  isAnchor: boolean;
  /** For each metric key, one value per snapshot date (null if data is missing). */
  valuesByMetric: Record<MetricKey, Array<number | null>>;
  /**
   * One boolean per snapshot column — true when the keyword had no real fetch on
   * that date and its metrics were carried forward from the prior fetch.
   * Displayed as a "\u2190" (←) indicator in the table and Excel export.
   */
  isForwardFilledBySnapshot: boolean[];
  /**
   * One boolean per snapshot column — true when the rank alone was carried
   * forward (no rank fetch that date, e.g. a failed SERP lookup). Marks the
   * Rank and Trend cells with "←" even when the volume was fetched.
   */
  isRankForwardFilledBySnapshot: boolean[];
};

/** Hover text for a cell marked "←". */
const FORWARD_FILL_HINT =
  "No fetch on this date — data carried forward from the last known snapshot";
const RANK_FORWARD_FILL_HINT =
  "No rank fetch on this date — rank carried forward from the last known snapshot";

/** Rank and Trend come from the rank; Volume and Difficulty don't. */
const isRankMetric = (metric: MetricKey): boolean =>
  metric === "ranking" || metric === "trend";

/**
 * Whether a cell shows "←", and which hint explains it. A whole-keyword
 * carry-over marks every metric; a rank-only carry-over marks Rank and Trend.
 */
const forwardFillFor = (
  row: TimelineTableRow,
  metric: MetricKey,
  colIdx: number
): { filled: boolean; hint?: string } => {
  if (row.isForwardFilledBySnapshot[colIdx]) {
    return { filled: true, hint: FORWARD_FILL_HINT };
  }
  if (isRankMetric(metric) && row.isRankForwardFilledBySnapshot[colIdx]) {
    return { filled: true, hint: RANK_FORWARD_FILL_HINT };
  }
  return { filled: false };
};

interface AnimationDatasetTableProps {
  snapshots: Snapshot[];
  /** Anchor keyword used in the export filename. */
  anchorKeyword: string;
  /** The date range the user selected, used in the export filename. */
  exportDateRange: [Date | null, Date | null];
}

/** Ordered list of metric sub-rows rendered beneath each keyword. */
const metricRows: Array<{ key: MetricKey; label: string }> = [
  { key: "volume", label: "Volume" },
  { key: "difficulty", label: "Difficulty" },
  { key: "ranking", label: "Rank" },
  { key: "trend", label: "Trend" },
];

/** Formats a snapshot date for a compact column header (e.g. "Mar 18"). */
const formatDateForTableHeader = (dateString: string): string => {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

/** Formats a metric value for display — handles locale formatting, sign prefix, and null. */
const formatMetricValue = (metric: MetricKey, value: number | null): string => {
  if (value === null || value === undefined) return "N/A";

  if (metric === "volume") {
    return Math.round(value).toLocaleString();
  }

  if (metric === "trend") {
    return value > 0 ? `+${value}` : `${value}`;
  }

  return `${Math.round(value)}`;
};

const AnimationDatasetTable: React.FC<AnimationDatasetTableProps> = ({
  snapshots,
  anchorKeyword,
  exportDateRange,
}) => {
  /**
   * Transforms the flat snapshots array into row data grouped by keyword.
   *
   * Each resulting TimelineTableRow has one entry per metric, and each metric
   * holds an array of values aligned to the column order (one value per snapshot).
   */
  const timelineTableRows = useMemo<TimelineTableRow[]>(() => {
    if (snapshots.length === 0) return [];

    // Step 1: Collect a stable, insertion-ordered list of unique keywords
    //         by walking every snapshot in order.
    const keywordOrder: string[] = [];
    const seenKeywords = new Set<string>();

    snapshots.forEach((snapshot) => {
      snapshot.data.forEach((item) => {
        if (!seenKeywords.has(item.keyword)) {
          seenKeywords.add(item.keyword);
          keywordOrder.push(item.keyword);
        }
      });
    });

    // Step 2: Build a Map<keyword, item> per snapshot for O(1) lookups in step 3.
    const snapshotKeywordMaps = snapshots.map(
      (snapshot) =>
        new Map(snapshot.data.map((item) => [item.keyword, item] as const))
    );

    // Step 3: For each keyword, collect its metric values and forward-fill flags across all snapshot dates.
    return keywordOrder.map((keyword) => {
      const valuesByMetric: Record<MetricKey, Array<number | null>> = {
        volume: [],
        difficulty: [],
        ranking: [],
        trend: [],
      };
      // One boolean per snapshot: true = this cell's data was forward-filled
      // (no real fetch on that date; metrics carried from the previous row).
      const isForwardFilledBySnapshot: boolean[] = [];
      const isRankForwardFilledBySnapshot: boolean[] = [];

      snapshotKeywordMaps.forEach((snapshotMap) => {
        const item = snapshotMap.get(keyword);
        valuesByMetric.volume.push(item?.volume ?? null);
        valuesByMetric.difficulty.push(item?.difficulty ?? null);
        valuesByMetric.ranking.push(item?.ranking ?? null);
        valuesByMetric.trend.push(item?.trend?.life ?? null);
        // isForwardFilled comes from KeywordWithMetadata (set by the API provider).
        isForwardFilledBySnapshot.push(item?.isForwardFilled === true);
        isRankForwardFilledBySnapshot.push(item?.isRankForwardFilled === true);
      });

      // Use the earliest snapshot containing this keyword to determine anchor status.
      const firstItem = snapshotKeywordMaps.find((snapshotMap) =>
        snapshotMap.has(keyword)
      )?.get(keyword);

      return {
        keyword,
        isAnchor: Boolean(firstItem?.isAnchor),
        valuesByMetric,
        isForwardFilledBySnapshot,
        isRankForwardFilledBySnapshot,
      };
    });
  }, [snapshots]);

  // Whether any frame carries a computed center (drives the extra row-group).
  const hasCenter = useMemo(
    () => snapshots.some((s) => s.center != null),
    [snapshots],
  );

  /**
   * Display string for the cluster-center row-group, per metric and snapshot
   * column. Mirrors the main Keywords Data table: Volume is a dash (it's the
   * weight, not a displayed value), Difficulty/Rank are the rounded weighted
   * values, and Trend is the between-frames delta rounded to 2 decimals (N/A on
   * the first frame). Shared by the on-screen table and the Excel export.
   */
  const centerCell = (metric: MetricKey, snapshotIndex: number): string => {
    const c = snapshots[snapshotIndex]?.center;
    if (!c) return "N/A";
    switch (metric) {
      case "volume":
        return "--";
      case "difficulty":
        return c.difficulty != null ? `${Math.round(c.difficulty)}` : "N/A";
      case "ranking":
        return c.ranking != null ? `${c.ranking}` : "N/A";
      case "trend": {
        const t = c.trend?.life;
        if (t === null || t === undefined) return "N/A";
        return formatCenterTrendNumber(t);
      }
      default:
        return "N/A";
    }
  };

  /** Builds and triggers an .xlsx download of the full dataset table. */
  const exportToExcel = () => {
    // Header row: Keyword, Metric, then one column per snapshot date.
    const header = [
      "Keyword",
      "Metric",
      ...snapshots.map((s) => formatDateForTableHeader(s.date)),
    ];

    // Data rows: one row per metric per keyword.
    const dataRows = timelineTableRows.flatMap((row) =>
      metricRows.map((metric, i) => [
        // Only the first metric row carries the keyword label.
        i === 0 ? (row.isAnchor ? `[ANCHOR] ${row.keyword}` : row.keyword) : "",
        metric.label,
        // Append " ←" for forward-filled cells so the indicator travels into Excel.
        ...row.valuesByMetric[metric.key].map((v, colIdx) => {
          // Trend has no meaningful value when the keyword is unranked (rank 0) in this column.
          const isUnrankedTrend =
            metric.key === "trend" && row.valuesByMetric.ranking[colIdx] === 0;
          const raw = isUnrankedTrend ? "-" : v ?? "";
          return forwardFillFor(row, metric.key, colIdx).filled ? `${raw} \u2190` : raw;
        }),
      ])
    );

    // Append the cluster-center row-group (one row per metric), then a note row.
    if (hasCenter) {
      metricRows.forEach((metric, i) => {
        dataRows.push([
          i === 0 ? "Cluster Weighted Average" : "",
          metric.label,
          ...snapshots.map((_s, colIdx) => centerCell(metric.key, colIdx)),
        ]);
      });
      dataRows.push([
        "Weighted by volume — a computed cluster overview, not a real keyword.",
      ]);
    }

    const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Keyword Trends");

    // Build filename: "{anchor}  {startDate} to {endDate}.xlsx"
    const anchorSlug = anchorKeyword.toLowerCase().replace(/\s+/g, "-");
    const fmt = (d: Date | null) =>
      d ? d.toISOString().split("T")[0] : "unknown";
    const filename = `${anchorSlug}  ${fmt(exportDateRange[0])} to ${fmt(exportDateRange[1])}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  if (snapshots.length === 0) {
    return null;
  }

  return (
    <div className="animation-dataset-table-container">
      <div className="animation-dataset-table-header">
        <h3>Generated Dataset Table</h3>
        <button className="export-excel-btn" onClick={exportToExcel}>
          Export to Excel
        </button>
      </div>

      <div className="animation-dataset-table-scroll">
        <table className="animation-dataset-table">
          <thead>
            <tr>
              <th className="dataset-header-keyword">Keyword</th>
              <th className="dataset-header-metric">Metric</th>
              {snapshots.map((snapshot) => (
                <th key={snapshot.date}>{formatDateForTableHeader(snapshot.date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timelineTableRows.map((row) => (
              <React.Fragment key={row.keyword}>
                {metricRows.map((metric, metricIndex) => (
                  <tr key={`${row.keyword}-${metric.key}`}>
                    {metricIndex === 0 && (
                      <th rowSpan={metricRows.length} className="dataset-keyword-cell">
                        {row.isAnchor && (
                          <span className="dataset-anchor-badge">Anchor</span>
                        )}
                        <br />
                        <span>{row.keyword}</span>
                      </th>
                    )}

                    <td className="dataset-metric-cell">{metric.label}</td>

                    {row.valuesByMetric[metric.key].map((value, dateIndex) => {
                      // Trend has no meaningful value when the keyword is unranked (rank 0) in this column.
                      const isUnrankedTrend =
                        metric.key === "trend" &&
                        row.valuesByMetric.ranking[dateIndex] === 0;
                      const fill = forwardFillFor(row, metric.key, dateIndex);
                      return (
                        <td
                          key={`${row.keyword}-${metric.key}-${dateIndex}`}
                          title={fill.hint}
                        >
                          {/* ← indicator: marks cells whose value was carried forward
                              (the whole keyword, or just its rank) from an earlier fetch */}
                          {fill.filled ? (
                            <span className="forward-fill-indicator">←</span>
                          ) : isUnrankedTrend ? (
                            "-"
                          ) : (
                            formatMetricValue(metric.key, value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {/* Cluster-center row-group — same conventions as the main table. */}
            {hasCenter && (
              <React.Fragment key="cluster-weighted-average">
                {metricRows.map((metric, metricIndex) => (
                  <tr
                    key={`center-${metric.key}`}
                    style={{ backgroundColor: "#f1eefe" }}
                  >
                    {metricIndex === 0 && (
                      <th
                        rowSpan={metricRows.length}
                        className="dataset-keyword-cell"
                        style={{ fontWeight: 700 }}
                      >
                        <span>Cluster Weighted Average</span>
                        <span
                          style={{
                            display: "block",
                            marginTop: "2px",
                            fontSize: "11px",
                            fontStyle: "italic",
                            fontWeight: 400,
                            color: "#6b6880",
                          }}
                        >
                          Weighted by volume — a computed cluster overview, not a
                          real keyword.
                        </span>
                      </th>
                    )}

                    <td className="dataset-metric-cell">{metric.label}</td>

                    {snapshots.map((_snapshot, dateIndex) => (
                      <td key={`center-${metric.key}-${dateIndex}`}>
                        {centerCell(metric.key, dateIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnimationDatasetTable;
